import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getSmartAdminPreview } from "@/lib/bousala/smart-preview.functions";

/**
 * Client side of the admin live preview. The server decides who is an admin;
 * this hook only asks (and only when a session exists, so anonymous visitors
 * never fire the request and SSR output is unchanged).
 */
export function useSmartAdminPreview(publicProduct: any | null) {
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setHasSession(Boolean(data.session));
    });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(Boolean(s)));
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const { data } = useQuery({
    queryKey: ["bousala", "smart-admin-preview"],
    queryFn: () => getSmartAdminPreview({ data: { product: "smart" } }),
    enabled: hasSession && !publicProduct,
    staleTime: 60_000,
    retry: false,
  });

  const previewProduct = hasSession ? (data as any)?.product ?? null : null;
  return {
    /** The Smart catalog row this viewer may act on — public row, or the admin preview. */
    product: publicProduct ?? previewProduct,
    /** True only while the product is hidden from everyone else. */
    isPreview: Boolean(!publicProduct && previewProduct),
  };
}

/** Small banner shown to an admin on pages where Smart is still hidden. */
export function SmartPreviewBanner({ show, lang = "ar" }: { show: boolean; lang?: "ar" | "en" }) {
  if (!show) return null;
  return (
    <p
      dir={lang === "en" ? "ltr" : "rtl"}
      className="mb-4 flex items-center gap-2 rounded-ds-pill bg-cream-100 px-4 py-2 font-display text-[14px] font-bold text-brown-700"
    >
      <Eye className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
      {lang === "en"
        ? "Admin preview — Smart Bousala is still hidden from visitors"
        : "معاينة المسؤول — البوصلة الذكية غير ظاهرة للزوار بعد"}
    </p>
  );
}
